import {
  BP_SCALE,
  type BasisPoints,
  bp,
  dec,
  type Decimal,
  kopecks,
  type Kopecks,
  minutes,
  type Minutes,
  ROUND_HALF_UP,
} from '@glory/kernel';

/** Пробелы любого вида (в том числе неразрывные из скопированных сумм) — разделители разрядов. */
const SPACES = /[\s\u00a0\u202f]+/g;

/** Множители сокращений: `120к`, `1,2м`, `3 млн`, `2 млрд`. */
const SUFFIXES: readonly [RegExp, number][] = [
  [/^(млрд|b)/, 1e9],
  [/^(млн|м|m)/, 1e6],
  [/^(тыс|к|k)/, 1e3],
];

/**
 * Калькулятор без `eval`: `+ - * /`, скобки, унарный минус, десятичная запятая или точка,
 * сокращения `к`/`м` у чисел. Ошибка разбора и деление на ноль — null.
 */
export function evaluateExpression(raw: string): Decimal | null {
  const text = raw
    .toLowerCase()
    .replace(SPACES, '')
    .replaceAll('×', '*')
    .replaceAll('х', '*')
    .replaceAll('÷', '/');
  let pos = 0;

  const peek = () => text[pos];
  const number = (): Decimal | null => {
    const match = /^(\d+(?:[.,]\d*)?|[.,]\d+)/.exec(text.slice(pos));
    if (!match) return null;
    pos += match[0].length;
    let value = dec(match[0].replace(',', '.'));
    const rest = text.slice(pos);
    const suffix = SUFFIXES.find(([re]) => re.test(rest));
    if (suffix) {
      pos += suffix[0].exec(rest)?.[0].length ?? 0;
      value = value.times(suffix[1]);
    }
    return value;
  };
  const factor = (): Decimal | null => {
    const c = peek();
    if (c === '-' || c === '+') {
      pos += 1;
      const inner = factor();
      return inner === null ? null : c === '-' ? inner.neg() : inner;
    }
    if (c === '(') {
      pos += 1;
      const inner = expression();
      if (inner === null || peek() !== ')') return null;
      pos += 1;
      return inner;
    }
    return number();
  };
  const term = (): Decimal | null => {
    let left = factor();
    while (left !== null && (peek() === '*' || peek() === '/')) {
      const op = peek();
      pos += 1;
      const right = factor();
      if (right === null || (op === '/' && right.isZero())) return null;
      left = op === '*' ? left.times(right) : left.div(right);
    }
    return left;
  };
  function expression(): Decimal | null {
    let left = term();
    while (left !== null && (peek() === '+' || peek() === '-')) {
      const op = peek();
      pos += 1;
      const right = term();
      if (right === null) return null;
      left = op === '+' ? left.plus(right) : left.minus(right);
    }
    return left;
  }

  if (text === '') return null;
  const result = expression();
  return result !== null && pos === text.length && result.isFinite() ? result : null;
}

/** Есть ли в тексте вычисление (`=1200*3`, `1200+300`), а не просто число со знаком. */
export function isExpression(raw: string): boolean {
  const text = raw.replace(SPACES, '');
  return text.startsWith('=') || /\d\s*[+\-*/×÷]/.test(text);
}

const stripCurrency = (raw: string) =>
  raw
    .toLowerCase()
    .replace(/(₽|руб(лей|ля|ль)?\.?|р\.?)$/, '')
    .replace(/^=/, '')
    .trim();

function toSafeInteger(value: Decimal): number | null {
  const n = value.toDecimalPlaces(0, ROUND_HALF_UP).toNumber();
  return Number.isSafeInteger(n) ? n : null;
}

/** Деньги: `120 000`, `1 200,50`, `120к`, `1,2м`, `=1200*3`, `3 500 ₽` → копейки. */
export function parseMoneyInput(raw: string): Kopecks | null {
  const value = evaluateExpression(stripCurrency(raw));
  if (value === null) return null;
  const n = toSafeInteger(value.times(100));
  return n === null ? null : kopecks(n);
}

const groupFormat = (fractionDigits: number) =>
  new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

/** Сумма в поле ввода: `122 600`, копейки — только если есть: `1 200,50`. */
export function formatMoneyInput(value: Kopecks): string {
  const rubles = dec(value).div(100);
  return groupFormat(rubles.isInteger() ? 0 : 2)
    .format(rubles.toNumber())
    .replace('-', '−');
}

/** Проценты → базисные пункты: `40`, `40 %`, `12,5` → 1250; точность — сотые процента. */
export function parsePercentInput(raw: string): BasisPoints | null {
  const value = evaluateExpression(raw.replace(/%$/, '').replace(/^=/, '').trim());
  if (value === null) return null;
  const n = toSafeInteger(value.times(100));
  return n === null ? null : bp(n);
}

export function formatPercentInput(value: BasisPoints): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(dec(value).div(100).toNumber());
}

/** Коэффициент → базисные пункты: `×1,3`, `x1.3`, `*1,3`, `1,3` → 13 000; точность — 4 знака. */
export function parseMultiplierInput(raw: string): BasisPoints | null {
  const value = evaluateExpression(raw.trim().replace(/^[×xх*]\s*/i, ''));
  if (value === null) return null;
  const n = toSafeInteger(value.times(BP_SCALE));
  return n === null ? null : bp(n);
}

export function formatMultiplierInput(value: BasisPoints): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 4, useGrouping: false }).format(
    dec(value).div(BP_SCALE).toNumber(),
  );
}

const HOUR = '(?:ч|час[а-я]*)';
const MINUTE = '(?:м|мин[а-я]*)';

/** Форматы времени работ: что совпало — сколько это минут. */
const HOURS_FORMATS: readonly [RegExp, (m: RegExpExecArray) => Decimal][] = [
  // 1:30
  [
    /^(\d{1,4}):([0-5]\d)$/,
    (m) =>
      dec(m[1] ?? 0)
        .times(60)
        .plus(m[2] ?? 0),
  ],
  // 1.5, 2ч, 1.5часа — число без единицы означает часы
  [new RegExp(`^(\\d+(?:\\.\\d+)?)${HOUR}?$`), (m) => dec(m[1] ?? 0).times(60)],
  // 90м, 45мин
  [new RegExp(`^(\\d+)${MINUTE}$`), (m) => dec(m[1] ?? 0)],
  // 1ч30, 1ч30м
  [
    new RegExp(`^(\\d+)${HOUR}(\\d+)${MINUTE}?$`),
    (m) =>
      dec(m[1] ?? 0)
        .times(60)
        .plus(m[2] ?? 0),
  ],
];

/**
 * Время работ → минуты: `1,5` (часы), `1:30`, `90м`, `45 мин`, `2ч`, `1ч 30м`, `1,5ч`.
 * Отрицательное и непонятное — null; доли минуты округляются.
 */
export function parseHoursInput(raw: string): Minutes | null {
  const text = raw.toLowerCase().replace(SPACES, '').replace(',', '.');
  for (const [re, toMinutes] of HOURS_FORMATS) {
    const match = re.exec(text);
    if (!match) continue;
    const n = toSafeInteger(toMinutes(match));
    return n === null ? null : minutes(n);
  }
  return null;
}

/** Время работ в поле ввода — часы десятичной дробью: 90 → `1,5`. */
export function formatHoursInput(value: Minutes): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2, useGrouping: false }).format(
    dec(value).div(60).toNumber(),
  );
}

/** Число: разряды пробелами, десятичная запятая или точка, можно выражение. */
export function parseNumberInput(raw: string): number | null {
  const value = evaluateExpression(raw.replace(/^=/, '').trim());
  return value === null ? null : value.toNumber();
}

export function formatNumberInput(value: number, fractionDigits = 2): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: fractionDigits })
    .format(value)
    .replace('-', '−');
}

/**
 * Разряды при вводе: `1234567` → `1 234 567`, курсор остаётся после той же цифры.
 * Текст с выражением или буквами не трогается.
 */
export function groupDigitsLive(text: string, caret: number): { text: string; caret: number } {
  const plain = /^([−-]?)([\d\s\u00a0\u202f]*)([.,]\d*)?$/.exec(text);
  if (!plain || isExpression(text)) return { text, caret };
  const [, sign = '', whole = '', fraction = ''] = plain;
  const digits = whole.replace(SPACES, '');
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
  const next = `${sign}${grouped}${fraction}`;
  // Сколько значащих символов (не пробелов) было до курсора — столько же оставить до него.
  const significant = text.slice(0, caret).replace(SPACES, '').length;
  let seen = 0;
  let position = 0;
  while (position < next.length && seen < significant) {
    if (!/[\s\u00a0]/.test(next[position] ?? '')) seen += 1;
    position += 1;
  }
  return { text: next, caret: position };
}
